import React, { useState } from 'react';

import { FormattedMessage } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import AddIcon from '@atlaskit/icon/core/add';
import Popup, { type ContentProps } from '@atlaskit/popup';
import { Flex } from '@atlaskit/primitives/compiled';
import Tooltip from '@atlaskit/tooltip';

import {
	getMaximumItemLimit,
	type LinksMenuExtensionActions,
} from '../../../../controllers/use-links-menu-parameters-and-actions';
import type { ITEM } from '../../../../types';
import { LinksMenuItemConfig } from '../links-menu-item-tree/links-menu-item/links-menu-item-config';

import i18n from './messages';
import { NewItemSelection } from './new-item-selection';

export type NewItemButtonProps = {
	extensionActions: LinksMenuExtensionActions;
	maxItems?: number;
	numberItems: number;
};

export const NewItemButton = ({
	extensionActions,
	numberItems,
}: NewItemButtonProps): React.JSX.Element => {
	const [isPopupOpen, setIsPopupOpen] = useState(false);

	const [isConfiguring, setIsConfiguring] = useState(false);
	const [newItemType, setNewItemType] = useState<ITEM | undefined>(undefined);

	const handleOnClose = () => {
		setIsPopupOpen(false);
		setIsConfiguring(false);
		setNewItemType(undefined);
	};
	const isAddDisabled = numberItems >= getMaximumItemLimit();

	const handleAddNewItemButtonClick = () => {
		setIsPopupOpen(!isPopupOpen);
		setIsConfiguring(false);
		setNewItemType(undefined);
	};

	return (
		<Popup
			isOpen={isPopupOpen}
			onClose={handleOnClose}
			placement="bottom-end"
			fallbackPlacements={['auto']}
			content={({ update }: ContentProps) =>
				isConfiguring && newItemType ? (
					<LinksMenuItemConfig
						extensionActions={extensionActions}
						handleClose={handleOnClose}
						update={update}
						newItemType={newItemType}
					/>
				) : (
					<NewItemSelection setIsConfiguring={setIsConfiguring} setNewItemType={setNewItemType} />
				)
			}
			trigger={(triggerProps) => (
				<Tooltip
					content={<FormattedMessage {...i18n.itemCreationDisabledTooltipText} />}
					canAppear={() => isAddDisabled}
				>
					<Button
						{...triggerProps}
						onClick={handleAddNewItemButtonClick}
						isSelected={isPopupOpen && !isConfiguring}
						isDisabled={isAddDisabled}
						shouldFitContainer
					>
						<Flex alignItems="center" justifyContent="center" gap="space.050">
							<AddIcon label="" />
							<FormattedMessage {...i18n.newItemButtonText} />
						</Flex>
					</Button>
				</Tooltip>
			)}
		/>
	);
};
